import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';

import { embryoServiceApi } from './api';
import {
  AddOutsideEmbryoInput,
  CreateClientEmbryosInput,
  EditEmbryoInput,
  EditOutsideEmbryoInput,
  EmbryoApiResponse,
  EmbryoGridV5Row,
  FreezeEntryInput,
  ImplantedEmbryoInput,
} from './types';

export function embryosBySitesQueryOptions(
  token: AccessToken,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: ['embryo', 'list-by-sites', searchParams.toString()],
    async queryFn() {
      const queryString = searchParams.toString();

      if (!token) {
        throw new Error('No access token available. Please log in.');
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
      if (
        !API_BASE_URL ||
        API_BASE_URL === 'undefined' ||
        API_BASE_URL === 'null'
      ) {
        throw new Error('VITE_API_BASE_URL is not defined or invalid');
      }

      const url = `${API_BASE_URL}/api/v3/embryo/embryos?${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    },
    enabled: !!token,
  });
}

export function embryosFromIndexQueryOptions(
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: ['embryo', 'read-model', searchParams.toString()],
    queryFn: () => embryoServiceApi.getEmbryosFromIndex(searchParams),
  });
}

export function embryosFromIndexV4QueryOptions(
  token: AccessToken,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: ['embryo', 'read-model-v4', searchParams.toString()],
    async queryFn(): Promise<{
      items: EmbryoApiResponse[];
      totalCount: number;
      queryMs: number | null;
    }> {
      const queryString = searchParams.toString();

      if (!token) {
        throw new Error('No access token available. Please log in.');
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
      if (
        !API_BASE_URL ||
        API_BASE_URL === 'undefined' ||
        API_BASE_URL === 'null'
      ) {
        throw new Error('VITE_API_BASE_URL is not defined or invalid');
      }

      const url = `${API_BASE_URL}/api/v4/embryo/index${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const header = response.headers.get('X-Query-Ms');
      const data = (await response.json()) as {
        items: EmbryoApiResponse[];
        totalCount: number;
      };
      return { ...data, queryMs: header ? Number(header) : null };
    },
    enabled: !!token,
  });
}

export function ivfTransitEmbryosFromIndexV4QueryOptions(
  token: AccessToken,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: [
      'embryo',
      'read-model-v4',
      'ivf-transit',
      searchParams.toString(),
    ],
    async queryFn(): Promise<{
      items: EmbryoApiResponse[];
      totalCount: number;
      queryMs: number | null;
    }> {
      const queryString = searchParams.toString();

      if (!token) {
        throw new Error('No access token available. Please log in.');
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
      if (
        !API_BASE_URL ||
        API_BASE_URL === 'undefined' ||
        API_BASE_URL === 'null'
      ) {
        throw new Error('VITE_API_BASE_URL is not defined or invalid');
      }

      const url = `${API_BASE_URL}/api/v4/embryo/ivf-transit/index${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const header = response.headers.get('X-Query-Ms');
      const data = (await response.json()) as {
        items: EmbryoApiResponse[];
        totalCount: number;
      };
      return { ...data, queryMs: header ? Number(header) : null };
    },
    enabled: !!token,
  });
}

async function fetchEmbryoGridV5(
  token: AccessToken,
  path: string,
  searchParams: URLSearchParams,
): Promise<{
  items: EmbryoGridV5Row[];
  totalCount: number;
  queryMs: number | null;
}> {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}${path}${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const header = response.headers.get('X-Query-Ms');
  const data = (await response.json()) as {
    items: EmbryoGridV5Row[];
    totalCount: number;
  };
  return { ...data, queryMs: header ? Number(header) : null };
}

export function embryosFromIndexV5QueryOptions(
  token: AccessToken,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: ['embryo', 'read-model-v5', searchParams.toString()],
    queryFn: () =>
      fetchEmbryoGridV5(token, '/api/v5/embryo/index', searchParams),
    enabled: !!token,
  });
}

export function ivfTransitEmbryosFromIndexV5QueryOptions(
  token: AccessToken,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: [
      'embryo',
      'read-model-v5',
      'ivf-transit',
      searchParams.toString(),
    ],
    queryFn: () =>
      fetchEmbryoGridV5(
        token,
        '/api/v5/embryo/ivf-transit/index',
        searchParams,
      ),
    enabled: !!token,
  });
}

export function ivfTransitEmbryosQueryOptions(
  token: AccessToken,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  return queryOptions({
    queryKey: [
      'embryo',
      'list-by-sites',
      'ivf-transit',
      searchParams.toString(),
    ],
    async queryFn() {
      const queryString = searchParams.toString();

      if (!token) {
        throw new Error('No access token available. Please log in.');
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
      if (
        !API_BASE_URL ||
        API_BASE_URL === 'undefined' ||
        API_BASE_URL === 'null'
      ) {
        throw new Error('VITE_API_BASE_URL is not defined or invalid');
      }

      const url = `${API_BASE_URL}/api/v3/embryo/ivf-transit?${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    },
    enabled: !!token,
  });
}

export const createOutsideEmbryoOptions = (
  searchParams: URLSearchParams = new URLSearchParams(),
) => ({
  queryKey: ['embryo', 'outside', 'list', searchParams.toString()],
  queryFn: () => embryoServiceApi.getOutsideEmbryos(searchParams),
  staleTime: 0,
});

export const useEditEmbryos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: EditEmbryoInput[]) =>
      embryoServiceApi.editEmbryos(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embryo'] });
    },
  });
};

export const useEditOutsideEmbryos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: EditOutsideEmbryoInput[]) =>
      embryoServiceApi.editOutsideEmbryos(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embryo'] });
    },
  });
};

export const useAddOutsideEmbryosV2 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddOutsideEmbryoInput) =>
      embryoServiceApi.addOutsideEmbryosV2(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['outside-embryos'],
      });
    },
  });
};

export const useAddOutsideEmbryos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddOutsideEmbryoInput) =>
      embryoServiceApi.addOutsideEmbryos(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['outside-embryos'],
      });
    },
  });
};

export const useGetEmbryoAnnotations = (embryoId: string | undefined) => {
  return useQuery({
    queryKey: ['embryo', 'annotations', embryoId],
    queryFn: () => embryoServiceApi.getAnnotations(embryoId!),
    enabled: !!embryoId,
  });
};

export const useCreateAnnotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      embryoId,
      noteText,
    }: {
      embryoId: string;
      noteText: string;
    }) => embryoServiceApi.createAnnotation(embryoId, noteText),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['embryo', 'annotations', variables.embryoId],
      });
    },
  });
};

export const useDeleteAnnotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      embryoId,
      annotationId,
    }: {
      embryoId: string;
      annotationId: string;
    }) => embryoServiceApi.deleteAnnotation(embryoId, annotationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['embryo', 'annotations', variables.embryoId],
      });
    },
  });
};

export const useGetEmbryoCalves = (embryoIds: string[] | undefined) => {
  return useQuery({
    queryKey: ['embryo', 'calves', embryoIds],
    queryFn: () => embryoServiceApi.getEmbryoCalves(embryoIds!),
    enabled: !!embryoIds && embryoIds.length > 0,
  });
};

export const useGetFreezeEntries = (outsideEmbryoId: string | undefined) => {
  return useQuery({
    queryKey: ['embryo', 'freeze-entries', outsideEmbryoId],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('embryoOutsideId', outsideEmbryoId!);
      return embryoServiceApi.getFreezeEntries(params);
    },
    enabled: !!outsideEmbryoId,
    refetchInterval: 10_000,
  });
};

export const useGetImplantedEmbryos = (outsideEmbryoId: string | undefined) => {
  return useQuery({
    queryKey: ['embryo', 'implanted', outsideEmbryoId],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('outsideEmbryoId', outsideEmbryoId!);
      return embryoServiceApi.getImplantedEmbryos(params);
    },
    enabled: !!outsideEmbryoId,
    refetchInterval: 10_000,
  });
};

export const useEditImplantedEmbryosMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: ImplantedEmbryoInput[]) =>
      embryoServiceApi.editImplantedEmbryos(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embryo'] });
    },
  });
};

export const useEditFreezeEntriesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: FreezeEntryInput[]) =>
      embryoServiceApi.editFreezeEntries(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embryo'] });
    },
  });
};

export const useCreateClientEmbryosMutation = () =>
  useMutation({
    mutationFn: (input: CreateClientEmbryosInput) =>
      embryoServiceApi.createClientEmbryos(input),
  });
